import React from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from 'utils/config';
import { getAnnouncementHeadingId } from 'utils/announcement/getHeadingId';
import { getHasAnnouncement } from 'utils/announcement/getHasAnnouncement';
import { useFetchMySharedContent, useFetchMyFeaturedContent } from 'actions/content';
import { useFetchMyBrowsePlaylists, useFetchMyRecommendedPlaylists } from 'actions/playlists';
import { useFetchBrowseTopics } from 'actions/topics';
import { useFetchAnnouncements } from 'actions/announcements';
import Button from 'components/Button';
import Heading from 'components/Heading';
import PageSection from 'components/PageSection';
import LandmarkLink from 'components/LandmarkLink';
import PageTitle from 'components/PageTitle';
import AnnouncementBanner from 'components/AnnouncementBanner';
import TopicList from '../browse/TopicList';
import HelixOnlyAccessWrapper from './HelixOnlyAccessWrapper';
import CollectionCarousel from './CollectionCarousel';
import BillboardAndCarousel from './BillboardAndCarousel';
import { HOME_IDS } from './utils';
function Home() {
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useFetchAnnouncement = useFetchAnnouncements({
      limit: 1
    }),
    items = _useFetchAnnouncement.items;
  var announcement = items === null || items === void 0 ? void 0 : items[0];
  var hasTopicsOnHome = config.topicsOnHome === 'tiles' || config.topicsOnHome === 'buttons';
  var _useFetchBrowseTopics = useFetchBrowseTopics({
      enabled: hasTopicsOnHome
    }),
    data = _useFetchBrowseTopics.data;
  var _ref = data || {
      subtopics: []
    },
    topics = _ref.subtopics;
  var _useFetchMySharedCont = useFetchMySharedContent(),
    shared = _useFetchMySharedCont.items;
  var _useFetchMyFeaturedCo = useFetchMyFeaturedContent(),
    featured = _useFetchMyFeaturedCo.items;
  var _useFetchMyBrowsePlay = useFetchMyBrowsePlaylists({
      sort: 'last_viewed',
      limit: 10
    }),
    lastViewed = _useFetchMyBrowsePlay.items;
  var _useFetchMyBrowsePlay2 = useFetchMyBrowsePlaylists({
      sort: 'trending',
      duration: 30,
      limit: 10
    }),
    trending = _useFetchMyBrowsePlay2.items;
  var _useFetchMyBrowsePlay3 = useFetchMyBrowsePlaylists({
      sort: 'latest',
      limit: 10
    }),
    latest = _useFetchMyBrowsePlay3.items;
  var _useFetchMyBrowsePlay4 = useFetchMyBrowsePlaylists({
      sort: 'random',
      limit: 10
    }),
    randomRecommendations = _useFetchMyBrowsePlay4.items,
    randomRecommdationsLoading = _useFetchMyBrowsePlay4.isLoading;
  var _useFetchMyRecommende = useFetchMyRecommendedPlaylists(),
    recommendations = _useFetchMyRecommende.items,
    recommendationsLoading = _useFetchMyRecommende.isLoading;
  var userRecommendations = !config.recommendations || !(recommendations !== null && recommendations !== void 0 && recommendations.length) && !recommendationsLoading && !randomRecommdationsLoading ? randomRecommendations : recommendations;
  var FEATURED_CONTENT_ID = HOME_IDS.FEATURED_CONTENT_ID,
    SHARED_ID = HOME_IDS.SHARED_ID,
    TOPICS_ID = HOME_IDS.TOPICS_ID,
    RECENTLY_ADDED_ID = HOME_IDS.RECENTLY_ADDED_ID,
    RECENTLY_VIEWED_ID = HOME_IDS.RECENTLY_VIEWED_ID,
    TRENDING_ID = HOME_IDS.TRENDING_ID,
    YOU_MIGHT_LIKE_ID = HOME_IDS.YOU_MIGHT_LIKE_ID;
  var hasLatest = (latest === null || latest === void 0 ? void 0 : latest.length) > 0;
  var hasTrending = (trending === null || trending === void 0 ? void 0 : trending.length) > 0;
  var hasShared = shared.length > 0;
  var hasFeatured = (featured === null || featured === void 0 ? void 0 : featured.length) > 0;
  var hasRecommendations = (userRecommendations === null || userRecommendations === void 0 ? void 0 : userRecommendations.length) > 0;
  var hasAnnouncement = getHasAnnouncement(announcement);
  var announcementLabelId = announcement && getAnnouncementHeadingId(announcement === null || announcement === void 0 ? void 0 : announcement.id);
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PageTitle, {
    visuallyHidden: true
  }, t('navbar_home')), hasAnnouncement && /*#__PURE__*/React.createElement(PageSection, {
    "aria-labelledby": announcementLabelId
  }, /*#__PURE__*/React.createElement(AnnouncementBanner, {
    announcement: announcement,
    isDismissable: true
  })), hasFeatured && /*#__PURE__*/React.createElement(PageSection, {
    "aria-labelledby": FEATURED_CONTENT_ID
  }, /*#__PURE__*/React.createElement(Heading, {
    as: "h2",
    size: "large",
    id: FEATURED_CONTENT_ID,
    mb: "s"
  }, t('featured_content_title')), /*#__PURE__*/React.createElement(BillboardAndCarousel, {
    items: featured,
    qaIdContext: FEATURED_CONTENT_ID
  })), hasShared && /*#__PURE__*/React.createElement(PageSection, {
    "aria-labelledby": SHARED_ID
  }, /*#__PURE__*/React.createElement(Heading, {
    as: "h2",
    size: "large",
    id: SHARED_ID,
    mb: "s"
  }, t('shared_with_you')), hasFeatured ? /*#__PURE__*/React.createElement(CollectionCarousel, {
    isShared: true,
    items: shared,
    qaIdContext: SHARED_ID
  }) : /*#__PURE__*/React.createElement(BillboardAndCarousel, {
    isShared: true,
    items: shared,
    qaIdContext: SHARED_ID
  })), hasTopicsOnHome && topics.length > 0 && /*#__PURE__*/React.createElement(PageSection, {
    "aria-labelledby": TOPICS_ID
  }, /*#__PURE__*/React.createElement(Heading, {
    as: "h2",
    size: "large",
    id: TOPICS_ID,
    mb: "s"
  }, t('content_type_topic_plural')), /*#__PURE__*/React.createElement(TopicList, {
    withBrowseButton: true,
    items: topics,
    variant: config.topicsOnHome
  })), hasTrending && /*#__PURE__*/React.createElement(PageSection, {
    "aria-labelledby": TRENDING_ID
  }, /*#__PURE__*/React.createElement(Heading, {
    as: "h2",
    size: "large",
    id: TRENDING_ID,
    mb: "s"
  }, t('trending_title')), hasFeatured || hasShared ? /*#__PURE__*/React.createElement(CollectionCarousel, {
    items: trending,
    qaIdContext: TRENDING_ID
  }) : /*#__PURE__*/React.createElement(BillboardAndCarousel, {
    items: trending,
    qaIdContext: TRENDING_ID
  })), hasRecommendations && /*#__PURE__*/React.createElement(PageSection, {
    "aria-labelledby": YOU_MIGHT_LIKE_ID
  }, /*#__PURE__*/React.createElement(Heading, {
    as: "h2",
    size: "large",
    id: YOU_MIGHT_LIKE_ID,
    mb: "s"
  }, t('you_might_like_title')), /*#__PURE__*/React.createElement(CollectionCarousel, {
    items: userRecommendations,
    qaIdContext: YOU_MIGHT_LIKE_ID
  })), (lastViewed === null || lastViewed === void 0 ? void 0 : lastViewed.length) > 0 && /*#__PURE__*/React.createElement(PageSection, {
    "aria-labelledby": RECENTLY_VIEWED_ID
  }, /*#__PURE__*/React.createElement(Heading, {
    as: "h2",
    size: "large",
    id: RECENTLY_VIEWED_ID,
    mb: "s"
  }, t('recently_viewed_title')), /*#__PURE__*/React.createElement(CollectionCarousel, {
    items: lastViewed,
    qaIdContext: RECENTLY_VIEWED_ID
  })), hasLatest && /*#__PURE__*/React.createElement(PageSection, {
    "aria-labelledby": RECENTLY_ADDED_ID
  }, /*#__PURE__*/React.createElement(Heading, {
    as: "h2",
    size: "large",
    id: RECENTLY_ADDED_ID,
    mb: "s"
  }, t('recently_added_title')), /*#__PURE__*/React.createElement(CollectionCarousel, {
    items: latest,
    qaIdContext: RECENTLY_ADDED_ID
  })), hasTrending && /*#__PURE__*/React.createElement(PageSection, null, /*#__PURE__*/React.createElement(LandmarkLink, {
    icon: "topic",
    headingLevel: "h2",
    title: t('my_learning_browse_cta_title'),
    button: /*#__PURE__*/React.createElement(Button, {
      as: NavLink,
      to: "browse",
      variant: "primary"
    }, t('actions_browse_all_playlists'))
  }, /*#__PURE__*/React.createElement("p", null, t('my_learning_browse_cta_content')))));
}
function ProtectedHome() {
  return /*#__PURE__*/React.createElement(HelixOnlyAccessWrapper, null, /*#__PURE__*/React.createElement(Home, null));
}
export default ProtectedHome;